import { ITSRelease1 } from "@consider-it/etsi-types";
import { t } from "i18next";
import WARNING_DANGEROUS_SITUATION from "../../assets/0_dangerous_situation.png";
import WARNING_OBSTACLE_ON_THE_ROAD from "../../assets/10_obstacle_on_the_road.png";
import WARNING_ANIMAL_ON_THE_ROAD from "../../assets/11_animal_on_the_road.png";
import WARNING_HUMAN_PRESENCE_ON_THE_ROAD from "../../assets/12_human_presence_on_the_road.png";
import WARNING_WRONG_WAY_DRIVER from "../../assets/14_wrong_way_driver.png";
import WARNING_WEATHER_CONDITION from "../../assets/17_adverse_weather_condition_strong_winds.png";
import WARNING_VISUAL_OBSTRUCTION from "../../assets/18_visual_obstruction.png";
import WARNING_CRITICAL_DRIVING_CONDITION from "../../assets/1_critical_driving_condition.png";
import WARNING_SLOW_VEHICLE from "../../assets/26_slow_vehicle.png";
import WARNING_DANGEROUS_END_OF_QUEUE from "../../assets/27_dangerous_end_of_queue.png";
import WARNING_ACCIDENT from "../../assets/2_accident.png";
import WARNING_ROADWORKS from "../../assets/3_roadworks.png";
import WARNING_SLIPPERY_ROAD from "../../assets/7_slippery_road.png";
import WARNING_VEHICLE_BREAKDOWN from "../../assets/91_vehicle_breakdown.png";
import WARNING_STATIONARY_VEHICLE from "../../assets/94_stationary_vehicle.png";
import WARNING_EMERGENCY_VEHICLE from "../../assets/95_emergency_vehicle.png";
import WARNING_STATIONARY_EMERGENCY_VEHICLE from "../../assets/sub1_stationary_emergency_vehicle.png";
import WARNING_BICYCLE from "../../assets/sub9_bicycle.png";
import { WarningDenm } from "../../services/V2XProvider";

type DenmContainerProps = {
  denm: WarningDenm;
};

const DenmContainer = ({ denm }: DenmContainerProps) => {
  return (
    <div className="denm-container" data-testid="denm-container">
      <img
        data-testid="denm-image"
        src={getDenmImage(denm)}
        alt="warning-sign"
      />
      <p data-testid="denm-label">{getWarningSignText(denm)}</p>
    </div>
  );
};

const getDenmImage = (denm: WarningDenm): string => {
  switch (denm.causeCode) {
    case ITSRelease1.CauseCodeType.TrafficCondition:
    case ITSRelease1.CauseCodeType.DangerousEndOfQueue:
      return  WARNING_DANGEROUS_END_OF_QUEUE;
    case ITSRelease1.CauseCodeType.Accident:
      return  WARNING_ACCIDENT;
    case ITSRelease1.CauseCodeType.Roadworks:
      return  WARNING_ROADWORKS;
    case ITSRelease1.CauseCodeType.AdverseWeatherConditionAdhesion:
    case ITSRelease1.CauseCodeType.HazardousLocationSurfaceCondition:
      return  WARNING_SLIPPERY_ROAD;
    case ITSRelease1.CauseCodeType.HazardousLocationObstacleOnTheRoad:
      return  WARNING_OBSTACLE_ON_THE_ROAD;
    case ITSRelease1.CauseCodeType.HazardousLocationAnimalOnTheRoad:
      return  WARNING_ANIMAL_ON_THE_ROAD;
    case ITSRelease1.CauseCodeType.HumanPresenceOnTheRoad:
      if (denm.subCauseCode === 9) {
        return  WARNING_BICYCLE;
      } else {
        return  WARNING_HUMAN_PRESENCE_ON_THE_ROAD;
      }
    case ITSRelease1.CauseCodeType.WrongWayDriving:
      return  WARNING_WRONG_WAY_DRIVER;
    case ITSRelease1.CauseCodeType.RescueAndRecoveryWorkInProgress:
      if (denm.subCauseCode === 0) {
        return  WARNING_VEHICLE_BREAKDOWN;
      } else if (denm.subCauseCode === 1) {
        return  WARNING_STATIONARY_EMERGENCY_VEHICLE;
      } else {
        return WARNING_DANGEROUS_SITUATION;
      }
    case ITSRelease1.CauseCodeType.AdverseWeatherConditionExtremeWeatherCondition:
      return  WARNING_WEATHER_CONDITION;
    case ITSRelease1.CauseCodeType.AdverseWeatherConditionVisibility:
    case ITSRelease1.CauseCodeType.AdverseWeatherConditionPrecipitation:
      return  WARNING_VISUAL_OBSTRUCTION;
    case ITSRelease1.CauseCodeType.SlowVehicle:
      return  WARNING_SLOW_VEHICLE;
    case ITSRelease1.CauseCodeType.StationaryVehicle:
      if (denm.subCauseCode === 0) {
        return  WARNING_STATIONARY_VEHICLE;
      } else if (denm.subCauseCode === 2) {
        return  WARNING_VEHICLE_BREAKDOWN;
      } else if (denm.subCauseCode === 3) {
        return  WARNING_ACCIDENT;
      } else {
        return WARNING_DANGEROUS_SITUATION;
      }
    case ITSRelease1.CauseCodeType.EmergencyVehicleApproaching:
      return  WARNING_EMERGENCY_VEHICLE;
    case ITSRelease1.CauseCodeType.DangerousSituation:
      return  WARNING_CRITICAL_DRIVING_CONDITION;
    default:
      return WARNING_DANGEROUS_SITUATION;
  }
};

const getWarningSignText = (denm: WarningDenm): string => {
  const distanceText = denm.distance > 0 ? ` in ${denm.distance} m` : "";

  switch (denm.causeCode) {
    case ITSRelease1.CauseCodeType.TrafficCondition:
      return t("denmTrafficCondition", { distance: distanceText });
    case ITSRelease1.CauseCodeType.Accident:
      return t("denmDanger", { distance: distanceText });
    case ITSRelease1.CauseCodeType.Roadworks:
      return t("denmRoadwork", { distance: distanceText });
    case ITSRelease1.CauseCodeType.AdverseWeatherConditionAdhesion:
    case ITSRelease1.CauseCodeType.HazardousLocationSurfaceCondition:
      return t("denmAquaplaning", { distance: distanceText });
    case ITSRelease1.CauseCodeType.HazardousLocationObstacleOnTheRoad:
      return t("denmObstacle", { distance: distanceText });
    case ITSRelease1.CauseCodeType.HazardousLocationAnimalOnTheRoad:
      return t("denmAnimal", { distance: distanceText });
    case ITSRelease1.CauseCodeType.HumanPresenceOnTheRoad:
      return t("denmPedestrian", { distance: distanceText });
    case ITSRelease1.CauseCodeType.WrongWayDriving:
      return t("denmWrongWayDriving", { distance: distanceText });
    case ITSRelease1.CauseCodeType.RescueAndRecoveryWorkInProgress:
      if (denm.subCauseCode === 0) {
        return t("denmBreakdown", { distance: distanceText });
      } else if (denm.subCauseCode === 1) {
        return t("denmStationaryEmergencyVehicle", { distance: distanceText });
      } else {
        return t("denmCaution", { distance: distanceText });
      }
    case ITSRelease1.CauseCodeType.AdverseWeatherConditionExtremeWeatherCondition:
      return t("denmExtremeWeatherCondition", { distance: distanceText });
    case ITSRelease1.CauseCodeType.AdverseWeatherConditionVisibility:
    case ITSRelease1.CauseCodeType.AdverseWeatherConditionPrecipitation:
      return t("denmWeatherCondition", { distance: distanceText });
    case ITSRelease1.CauseCodeType.SlowVehicle:
      return t("denmSlowVehicle", { distance: distanceText });
    case ITSRelease1.CauseCodeType.DangerousEndOfQueue:
      return t("denmQueueEnd", { distance: distanceText });
    case ITSRelease1.CauseCodeType.StationaryVehicle:
      if (denm.subCauseCode === 0) {
        return t("denmStationaryVehicle", { distance: distanceText });
      } else if (denm.subCauseCode === 2) {
        return t("denmBrokenDown", { distance: distanceText });
      } else if (denm.subCauseCode === 3) {
        return t("denmDanger", { distance: distanceText });
      } else {
        return t("denmCaution", { distance: distanceText });
      }
    case ITSRelease1.CauseCodeType.DangerousSituation:
      return t("denmCriticalCondition", { distance: distanceText });
    default:
      return t("denmCaution", { distance: distanceText });
  }
};

export default DenmContainer;
